// src/components/providerAssignments/MemberProviderAccessSection.tsx
//
// Phase A5.6 — "Provider access" row section for Circle → People.
//
// ============================================================
// THIS IS NOT THE CIRCLE PIN UI.
// ============================================================
// Rendered directly beneath <MemberProviderChips /> (the pin), and the
// visual separation is deliberate: the two look different, are labelled
// differently, and never share a control. Merging them in the UI would
// teach the operator a false mental model, because the underlying
// systems genuinely differ:
//
//   Provider access (here)  → Central `provider_access_grants`
//                             "may USE provider X across suite apps"
//                             Head of the exact Team grants/revokes
//
//   Circle routing (chips)  → Outbound `workspace_provider_user_assignments`
//                             "prefer provider X when acting from this Circle"
//
// Granting access does NOT set a pin, and it does NOT confer
// `phone.call`. A member with a grant but no `phone.call` still cannot
// dial — the capability is enforced independently on the voice route.

import { useMemo, useState, type ReactNode } from 'react';
import {
  useGetMemberProviderAccessQuery,
  useGetMyProviderAccessQuery,
  useGrantMemberProviderAccessMutation,
  useRevokeMemberProviderAccessMutation,
  type ProviderAccessGrantRow,
} from '../../api/circlesApi';

/** Channels the Central grant API accepts. */
export const GRANT_CHANNELS = ['voice', 'sms', 'email'] as const;
export type GrantChannel = (typeof GRANT_CHANNELS)[number];

export interface ProviderCatalogEntry {
  /** Outbound workspace-provider id — the grant's subject. */
  id: string;
  displayName: string;
  channelType: string;
  status?: string | null;
}

export interface MemberProviderAccessSectionProps {
  circleId: string;
  memberUserId: string;
  memberLabel: string;
  /**
   * Whether the VIEWER is an active Head of this Circle's exact Central
   * Team. Controls affordances only — the server re-checks Head status
   * and Central re-authorizes off the JWT, so a forged `true` here
   * yields 403, not a grant.
   */
  isHead: boolean;
  /**
   * Whether this row belongs to the VIEWER. A5.7: a non-Head with a
   * grant needs somewhere to see what they may use, so their own row
   * renders a read-only list sourced from `/provider-access/me` (which
   * Central scopes to the JWT `sub`). Non-Head rows that are not the
   * viewer's own render nothing at all — a Manager has no business
   * seeing, or being told about, a colleague's entitlements.
   */
  isSelf: boolean;
  /** Workspace provider catalog used to label ids and offer grants. */
  providers: ProviderCatalogEntry[];
  /** Channel this section manages. Defaults to voice (A5.6 scope). */
  channelType?: GrantChannel;
}

export default function MemberProviderAccessSection({
  circleId,
  memberUserId,
  memberLabel,
  isHead,
  isSelf,
  providers,
  channelType = 'voice',
}: MemberProviderAccessSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Non-Heads never issue the cross-user read: Central would 403 it.
  const { data, isLoading } = useGetMemberProviderAccessQuery(
    { circleId, memberUserId, channelType },
    { skip: !isHead },
  );

  // A5.7 self-read. Fires only for a non-Head looking at their OWN row.
  // Heads already have the cross-user read above, so issuing both would
  // be a redundant round-trip. Central derives the user from the JWT
  // `sub`, so this endpoint cannot be pointed at anyone else.
  const selfQuery = useGetMyProviderAccessQuery(
    { circleId, channelType },
    { skip: isHead || !isSelf },
  );

  const selfEntitledIds: string[] = useMemo(
    () =>
      (selfQuery.data?.entitled ?? [])
        .filter((e) => e.channelType === channelType)
        .map((e) => e.workspaceProviderId),
    [selfQuery.data, channelType],
  );

  const [grant, { isLoading: granting }] = useGrantMemberProviderAccessMutation();
  const [revoke, { isLoading: revoking }] = useRevokeMemberProviderAccessMutation();

  const activeGrants: ProviderAccessGrantRow[] = useMemo(
    () => (data?.grants ?? []).filter((g) => g.status === 'active'),
    [data],
  );

  const grantByProviderId = useMemo(() => {
    const m = new Map<string, ProviderAccessGrantRow>();
    for (const g of activeGrants) m.set(g.workspaceProviderId, g);
    return m;
  }, [activeGrants]);

  const channelProviders = useMemo(
    () => providers.filter((p) => p.channelType === channelType),
    [providers, channelType],
  );

  async function handleGrant(workspaceProviderId: string) {
    setError(null);
    try {
      await grant({
        circleId,
        memberUserId,
        workspaceProviderId,
        channelType,
      }).unwrap();
    } catch (e) {
      setError(readError(e));
    }
  }

  async function handleRevoke(grantId: string) {
    setError(null);
    try {
      await revoke({ circleId, memberUserId, grantId }).unwrap();
    } catch (e) {
      setError(readError(e));
    }
  }

  const busy = granting || revoking;

  // A5.7 — a non-Head viewing SOMEONE ELSE'S row gets nothing. Rendering
  // "only the Head can change this" on every colleague's row was noise
  // that implied the viewer had a stake in other people's entitlements.
  if (!isHead && !isSelf) return null;

  // A5.7 — non-Head viewing THEIR OWN row: strictly read-only. No grant,
  // revoke, expand, credential, or configuration affordance exists on
  // this branch. Provider access is a USAGE entitlement; administration
  // lives in Settings → Providers and stays owner/admin-only.
  if (!isHead && isSelf) {
    return (
      <div
        className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2"
        data-testid={`provider-access-self-${memberUserId}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-900">
            Provider access
          </span>
          <span className="text-[11px] text-indigo-700/70">
            providers you can use
          </span>
        </div>
        {selfQuery.isLoading && (
          <p className="mt-1 text-xs text-indigo-900/60">Loading your access…</p>
        )}
        {!selfQuery.isLoading && selfEntitledIds.length === 0 && (
          <p
            className="mt-1 text-xs text-indigo-900/60"
            data-testid={`provider-access-self-empty-${memberUserId}`}
          >
            No {channelType} providers assigned to you yet. Your Team Head
            grants provider access.
          </p>
        )}
        {!selfQuery.isLoading && selfEntitledIds.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {selfEntitledIds.map((pid) => (
              <li
                key={pid}
                className="flex items-center gap-1.5 text-xs text-indigo-950"
                data-testid={`provider-access-self-chip-${pid}`}
              >
                <span aria-hidden="true" className="text-emerald-600">
                  ✓
                </span>
                <span className="truncate">{labelFor(pid, providers)}</span>
                <span className="text-[10px] text-indigo-700/70">Granted</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-2 rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2"
      data-testid={`provider-access-section-${memberUserId}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-900">
            Provider access
          </span>
          <span
            className="text-[11px] text-indigo-700/70"
            title="Grants let this member USE a provider across LevelUp apps. This is separate from the Circle routing preference below."
          >
            across apps
          </span>
        </div>
        {isHead && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-indigo-700 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 py-0.5"
            data-testid={`manage-provider-access-${memberUserId}`}
            aria-expanded={expanded}
          >
            {expanded ? 'Done' : 'Manage provider access'}
          </button>
        )}
      </div>

      {isHead && isLoading && (
        <p className="mt-1 text-xs text-indigo-900/60">Loading access…</p>
      )}

      {isHead && !isLoading && activeGrants.length === 0 && !expanded && (
        <p
          className="mt-1 text-xs text-indigo-900/60"
          data-testid={`provider-access-empty-${memberUserId}`}
        >
          No provider access granted.
        </p>
      )}

      {isHead && !isLoading && activeGrants.length > 0 && !expanded && (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {activeGrants.map((g) => (
            <li
              key={g.id}
              className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[11px]"
              data-testid={`provider-access-chip-${g.workspaceProviderId}`}
            >
              {labelFor(g.workspaceProviderId, providers)} · Granted
            </li>
          ))}
        </ul>
      )}

      {isHead && expanded && (
        <div className="mt-2 space-y-1">
          <p className="text-[11px] text-indigo-900/70">
            {channelType} access for {memberLabel}
          </p>
          {channelProviders.length === 0 && (
            <p className="text-xs text-indigo-900/60">
              No {channelType} providers in this workspace.
            </p>
          )}
          <ProviderGrantRows
            providers={channelProviders}
            grantIdFor={(pid) => grantByProviderId.get(pid)?.id}
            busy={busy}
            onGrant={(pid) => void handleGrant(pid)}
            onRevoke={(grantId) => void handleRevoke(grantId)}
          />
        </div>
      )}

      {error && (
        <p
          className="mt-1 text-[11px] text-rose-700"
          data-testid={`provider-access-error-${memberUserId}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The per-provider Grant / Revoke list — the presentational core shared by
 * Circle → People and Settings → Members → Teams (RA-4).
 *
 * It owns no data and no mutation. The host decides which providers to list,
 * which are granted, and what Grant and Revoke do, so the two surfaces can
 * sit on different routes (Circle-keyed vs Team-keyed) without duplicating
 * the UI. Every optional prop defaults to the Circle surface's original
 * behaviour — same test ids, same markup, every Grant enabled — so extracting
 * this changed nothing there.
 */
export function ProviderGrantRows({
  providers,
  grantIdFor,
  busy,
  onGrant,
  onRevoke,
  canGrant,
  renderMeta,
  testIdPrefix = '',
}: {
  providers: ProviderCatalogEntry[];
  /** The ACTIVE grant id for a provider, or undefined when not granted. */
  grantIdFor: (providerId: string) => string | undefined;
  busy: boolean;
  onGrant: (providerId: string) => void;
  onRevoke: (grantId: string) => void;
  /** When it returns false, Grant is shown disabled. Default: always grantable. */
  canGrant?: (provider: ProviderCatalogEntry) => boolean;
  /** Extra safe detail after the name (e.g. a Default badge). */
  renderMeta?: (provider: ProviderCatalogEntry) => ReactNode;
  /** Prepended to every test id, so two hosts on one page stay distinct. */
  testIdPrefix?: string;
}) {
  return (
    <ul className="divide-y divide-indigo-100">
      {providers.map((p) => {
        const grantId = grantIdFor(p.id);
        const isGranted = Boolean(grantId);
        const grantable = canGrant ? canGrant(p) : true;
        return (
          <li
            key={p.id}
            className="flex items-center justify-between gap-2 py-1"
            data-testid={`${testIdPrefix}provider-access-row-${p.id}`}
          >
            <span className="text-xs text-indigo-950 truncate">
              {p.displayName}
              {p.status && p.status !== 'active' && (
                <span className="ml-1 text-[10px] text-amber-700">
                  ({p.status})
                </span>
              )}
              {renderMeta?.(p)}
            </span>
            {isGranted ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => onRevoke(grantId!)}
                className="text-[11px] font-medium text-rose-700 hover:text-rose-900 rounded px-2 py-0.5 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-rose-500"
                data-testid={`${testIdPrefix}revoke-provider-access-${p.id}`}
              >
                Revoke
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || !grantable}
                onClick={() => onGrant(p.id)}
                className="text-[11px] font-medium text-indigo-700 hover:text-indigo-900 rounded px-2 py-0.5 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid={`${testIdPrefix}grant-provider-access-${p.id}`}
              >
                Grant access
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Flattens the `GET /circles/:id/providers` integrations payload into a
 * per-channel provider catalog. Mirrors the derivation in
 * ManageMemberProvidersModal — same source, same safe fields (id, label,
 * status), never credentials — so the grant picker and the pin picker
 * can never disagree about which providers exist.
 */
export function deriveProviderCatalog(
  integrations:
    | ReadonlyArray<{
        settings?: unknown;
        displayName?: string | null;
        appKey?: string | null;
        status?: string | null;
      }>
    | undefined,
): ProviderCatalogEntry[] {
  const out: ProviderCatalogEntry[] = [];
  for (const integration of integrations ?? []) {
    const settings = (integration.settings ?? {}) as {
      sync?: {
        channels?: Array<{
          outboundProviderId?: string;
          type?: string;
          displayName?: string | null;
          status?: string | null;
        } | null>;
      };
    };
    const channels = Array.isArray(settings.sync?.channels)
      ? settings.sync!.channels!
      : [];
    for (const ch of channels) {
      const pid = ch?.outboundProviderId;
      const type = ch?.type;
      if (typeof pid !== 'string' || !pid) continue;
      if (!(GRANT_CHANNELS as readonly string[]).includes(type ?? '')) continue;
      out.push({
        id: pid,
        displayName:
          ch?.displayName || integration.displayName || integration.appKey || 'Provider',
        channelType: type as string,
        status: ch?.status ?? integration.status ?? null,
      });
    }
  }
  return out;
}

function labelFor(providerId: string, providers: ProviderCatalogEntry[]): string {
  return providers.find((p) => p.id === providerId)?.displayName ?? providerId;
}

/**
 * Surfaces Central's stable error CODE rather than a raw message —
 * INSUFFICIENT_AUTHORITY is the one an operator actually needs to see,
 * because it means "you are not Head of this Team", not "try again".
 *
 * Exported (unchanged) so the Settings Team-lane surface can fall back to
 * exactly the wording this surface already uses.
 */
export function readError(e: unknown): string {
  const data = (e as { data?: { error?: string } } | undefined)?.data;
  const code = data?.error;
  if (code === 'INSUFFICIENT_AUTHORITY') {
    return 'Only the Head of this Team can change provider access.';
  }
  if (code === 'PROVIDER_NOT_KNOWN') return 'That provider is not available in this workspace.';
  if (code === 'BAD_CHANNEL') return 'Unsupported channel.';
  if (code) return code;
  return 'Could not update provider access.';
}
